import type { TimerEntryOutput } from "@orsane/schema/timers";
import { parseISO } from "date-fns";
import { Pause, Play, Trash2 } from "lucide-react";
import { memo, useState } from "react";
import { DurationInput } from "@/components/DurationInput";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { minutesToSeconds, SECONDS_PER_MINUTE } from "@/lib/duration";
import { formatClock } from "@/lib/format";
import { cn } from "@/lib/utils";
import { RunningClock } from "./RunningClock";
import { useElapsedSeconds } from "./useElapsedSeconds";

/**
 * Module scope so the identity is stable — DurationInput re-renders its display
 * whenever this prop changes.
 */
const clockFromMinutes = (minutes: number | null) => formatClock(minutesToSeconds(minutes ?? 0));

type TimerEntryRowProps = {
    entry: TimerEntryOutput;
    onPlay: (entryId: number) => void;
    onPause: (entryId: number) => void;
    onDelete: (entryId: number) => void;
    onChangeDuration: (entryId: number, seconds: number) => void;
    disabled?: boolean;
    /** Entries belong to whoever tracked them; only their owner may change them. */
    isOwn: boolean;
};

export const TimerEntryRow = memo(function TimerEntryRow({
    entry,
    onPlay,
    onPause,
    onDelete,
    onChangeDuration,
    disabled,
    isOwn,
}: TimerEntryRowProps) {
    const [isEditing, setIsEditing] = useState(false);
    const { formatShortDate } = useDateFormatter();
    const isRunning = entry.startedAt !== null;
    const elapsed = useElapsedSeconds(entry.accumulatedSeconds, entry.startedAt);

    const isLocked = disabled || !isOwn;

    const handleDurationChange = (minutes: number | null) => {
        setIsEditing(false);
        if (minutes === null) return;
        const seconds = minutesToSeconds(minutes);
        if (seconds === entry.accumulatedSeconds) return;
        onChangeDuration(entry.id, seconds);
    };

    return (
        // Matches a command item: same padding, radius and hover, with the row
        // actions revealed on hover the way the task rows do.
        <div className="group/timer-entry flex items-center gap-2 rounded-sm px-2 py-0.5">
            <span className="w-20 shrink-0 truncate text-orsane-secondary">
                {formatShortDate(parseISO(entry.entryDate))}
            </span>

            <div className="min-w-0 flex-1 tabular-nums whitespace-nowrap">
                {/* A recording entry shows the live clock and cannot be edited: the running
                    segment would race whatever was typed. The server rejects it too. */}
                {isRunning ? (
                    <span className="flex items-center gap-1.5 text-orsane-primary">
                        <RunningClock />
                        {formatClock(elapsed)}
                    </span>
                ) : isEditing ? (
                    <DurationInput
                        autoFocus
                        // h-7 keeps the row exactly as tall as it is when reading, so
                        // opening the editor does not shift the rows below it.
                        className="h-7 rounded-sm border-input/30 bg-input/30 px-1 shadow-none"
                        // Fractional minutes, not floored: the row shows seconds, so the
                        // editor has to be able to give them back unchanged.
                        value={entry.accumulatedSeconds / SECONDS_PER_MINUTE}
                        formatValue={clockFromMinutes}
                        onChange={handleDurationChange}
                        onBlur={() => setIsEditing(false)}
                    />
                ) : (
                    <button
                        type="button"
                        className="-mx-1 w-full rounded-sm px-1 text-left hover:bg-background disabled:cursor-default"
                        onClick={() => setIsEditing(true)}
                        disabled={isLocked}
                    >
                        {formatClock(entry.accumulatedSeconds)}
                    </button>
                )}
            </div>

            {/* The controls stay visible rather than revealing on hover: the list is short,
                and play/pause is the whole point of the row. */}
            <div className="flex shrink-0 items-center gap-0.5">
                {/* Play/pause sits closest to the duration: it is the common action, and
                    delete is kept furthest from the pointer's resting place. */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    className={cn(
                        isRunning ? "text-orsane-overdue" : "text-orsane-placeholder hover:text-orsane-primary"
                    )}
                    onClick={() => (isRunning ? onPause(entry.id) : onPlay(entry.id))}
                    disabled={isLocked}
                    aria-label={isRunning ? "Pause timer" : "Start timer"}
                >
                    {isRunning ? <Pause /> : <Play />}
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-orsane-placeholder hover:text-orsane-overdue"
                    onClick={() => onDelete(entry.id)}
                    disabled={isLocked || isRunning}
                    aria-label="Delete time entry"
                >
                    <Trash2 />
                </Button>
            </div>
        </div>
    );
});
